import { Texture, TextureOptions } from "../backends/webgl/texture"
import {
	DenoiseTextures,
	WaveletTextures,
	waveletDenoiseOption,
} from "../pipeline/passes/wavelet-denoise-pass"
import { Renderer } from "../pipeline/renderer"

export function initDenoiseTextures(renderer: Renderer): DenoiseTextures {
	if (renderer.denoiseTextures) {
		return renderer.denoiseTextures
	}

	const denoiseLumTexture = renderer.backend.createTexture({
		label: "denoise.lumTexture",
		format: "r16float",
	})
	const denoiseColorTexture = renderer.backend.createTexture({
		label: "denoise.colorTexture",
		format: "rg16float",
	})

	renderer.denoiseTextures = { denoiseLumTexture, denoiseColorTexture }

	return renderer.denoiseTextures
}

function initWaveletTextures(renderer: Renderer, maxLevels = 6): WaveletTextures {
	if (renderer.waveletTextures) {
		return renderer.waveletTextures
	}

	const textureOptions: TextureOptions = {
		label: "untitledTexture",
		format: renderer.backend.textureFormat,
	}

	const lumLevels: Texture[] = []

	// re-use the buffer to save memory
	lumLevels.push(renderer.dualBuffers.readBuffer)
	lumLevels.push(renderer.dualBuffers.writeBuffer)
	lumLevels.push(renderer.blurBuffers.readBuffer)
	for (let i = 3; i < maxLevels; i++)
		lumLevels.push(
			renderer.backend.createTexture({
				...textureOptions,
				label: `denoise.lumLevelTexture.${i}`,
			}),
		)

	const singleChannelTexture = renderer.backend.createTexture({
		label: "denoise.singleChannelTexture",
		format: "r16float",
	})
	const tempW2H2 = renderer.blurBuffers.writeBuffer // re-use the buffer to save memory

	const waveletTextures: WaveletTextures = {
		lumLevels,
		singleChannelTexture,
		tempW2H2,
	}

	renderer.waveletTextures = waveletTextures

	return waveletTextures
}

export function cleanupWaveletTextures(renderer: Renderer) {
	if (!renderer.waveletTextures) {
		return
	}

	renderer.waveletTextures.singleChannelTexture.delete()

	for (let i = 3; i < renderer.waveletTextures.lumLevels.length; i++) {
		renderer.waveletTextures?.lumLevels[i]?.delete()
	}

	renderer.waveletTextures = undefined
}

export function denoiseTextureWaveletChunked(
	renderer: Renderer,
	inputTexture: Texture,
	denoiseTextures: DenoiseTextures,
	option: waveletDenoiseOption,
) {
	const waveletTextures = initWaveletTextures(renderer)

	renderer.waveletDenoisePass.render(inputTexture, {
		...option,
		denoiseTextures,
		waveletTextures,
	})
}

export function denoiseTextureWavelet(renderer: Renderer, option: waveletDenoiseOption) {
	const { width, height } = renderer.inputTexture
	const denoiseTextures = initDenoiseTextures(renderer)
	const waveletTextures = initWaveletTextures(renderer)

	const width2 = Math.floor((width + 1) / 2)
	const height2 = Math.floor((height + 1) / 2)

	denoiseTextures.denoiseLumTexture.updateSize(width, height)
	denoiseTextures.denoiseColorTexture.updateSize(width2, height2)

	renderer.waveletDenoisePass.render(renderer.inputTexture, {
		...option,
		denoiseTextures,
		waveletTextures,
	})
}
